import clsx from 'clsx';
import { createPortal } from 'react-dom';
import { CSSProperties, MouseEvent, PropsWithChildren, ReactNode, useEffect, useRef, useState } from 'react';

import { Icon } from '../icon';
import { Divider } from '../divider';
import { Button, ButtonProps } from '../button';

import { useElementResize } from '../../hooks/use-element-resize';
import { useTransitionMark } from '../../hooks/use-transition-mark';

import { toRem } from '../../utils/ui';
import { conditionalSpread } from '../../utils/helper-utils';

import styles from './modal.module.scss';

// Matches the longest close transition in modal.module.scss.
const CLOSE_TRANSITION_MS = 300;

export const Modal = (props: ModalProps) => {
  const {
    id,
    open,
    title,
    onOpen,
    gap = 16,
    onClose,
    onClosed,
    children,
    freeForm,
    maxWidth,
    className,
    primaryButton,
    tertiaryButton,
    onClickOutside,
    closeIconSize,
    holderClassName,
    secondaryButton,
    hasFooter = true,
    topDivider = true,
    closeOnEscape = true,
    bottomDivider = true,
    decoupledScroll = false,
    closeOnClickOutside = true,
    modalContentHolderClassName,
    ...rest
  } = props;

  useEffect(() => {
    open && onOpen?.();
  }, [open, onOpen]);

  // CSS transitions rather than motion: its WAAPI opacity/transform animations make Chrome on macOS
  // re-present stale frames, flashing an earlier screen across the whole window (DM-1559).
  const holderRef = useRef<HTMLDivElement>(null);
  const [wasOpen, setWasOpen] = useState(open);
  const [closing, setClosing] = useState(false);
  if (wasOpen !== open) {
    setWasOpen(open);
    setClosing(!open);
  }

  const onClosedRef = useRef(onClosed);
  useEffect(() => {
    onClosedRef.current = onClosed;
  }, [onClosed]);

  useEffect(() => {
    if (!closing) return;
    const timeout = setTimeout(() => {
      setClosing(false);
      onClosedRef.current?.();
    }, CLOSE_TRANSITION_MS);
    return () => clearTimeout(timeout);
  }, [closing]);

  useTransitionMark(holderRef, 'open', open);

  const { ref, clientHeight } = useElementResize();

  useEffect(() => {
    const onKeyDown = (e: any) => {
      e.key === 'Escape' && onClose?.();
    };

    if (open) {
      closeOnEscape && window.addEventListener('keydown', onKeyDown);
    } else {
      closeOnEscape && window.removeEventListener('keydown', onKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, closeOnEscape, onClose]);

  const handleOutsideClick = () => {
    onClickOutside?.();
    closeOnClickOutside && onClose?.();
  };

  const handleScrollAreaClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!decoupledScroll || event.target !== event.currentTarget) {
      return;
    }

    handleOutsideClick();
  };

  return createPortal(
    <>
      {(open || closing) && (
        <div
          ref={holderRef}
          id={decoupledScroll ? undefined : id}
          className={clsx(styles.modalHolder, {
            modal: !decoupledScroll,
            [styles.decoupledScroll]: decoupledScroll,
          })}
          {...rest}
        >
          <div className={styles.modalBackdrop} onClick={!decoupledScroll ? handleOutsideClick : undefined} />
          <div
            id={decoupledScroll ? id : undefined}
            className={clsx(styles.modalScrollArea, {
              [styles.decoupledScrollArea]: decoupledScroll,
              modal: decoupledScroll,
            })}
            onClick={handleScrollAreaClick}
          >
            <div
              ref={ref}
              className={clsx(styles.modal, holderClassName, {
                [styles.fullWidth]: !!maxWidth,
              })}
              style={{
                ...conditionalSpread({ maxWidth: toRem(maxWidth) }, !!maxWidth),
                ...conditionalSpread({ ['--scroll-height']: toRem(clientHeight) }, !!clientHeight),
              }}
            >
              {freeForm ? (
                children
              ) : (
                <div
                  className={clsx(styles.modalContent, modalContentHolderClassName, {
                    [styles.topDivider]: topDivider,
                    [styles.bottomDivider]: bottomDivider,
                  })}
                  style={{ ['--gap']: toRem(gap) } as CSSProperties}
                >
                  <ul className={styles.modalHeader}>
                    <li></li>
                    <li>{title}</li>
                    <li>
                      <Icon
                        size={closeIconSize || 16}
                        onClick={onClose}
                        type="cancel-01"
                        className={styles.closeIcon}
                        data-testid="modal-close-btn"
                      />
                    </li>
                  </ul>
                  {topDivider && <Divider orientation="horizontal" className={styles.divider} />}
                  <div className={clsx(styles.modalInnerContent, className)}>{children}</div>
                  {hasFooter && (
                    <div className={styles.modalFooter}>
                      <Divider orientation="horizontal" className={styles.divider} />
                      <div className={styles.mfButtons}>
                        <Button
                          tabIndex={1}
                          onClick={onClose}
                          appearance="clear"
                          cornerRadius="rounded"
                          data-testid="modal-secondary-btn"
                          {...secondaryButton}
                        >
                          {secondaryButton?.children || 'Cancel'}
                        </Button>
                        {!!tertiaryButton && (
                          <Button tabIndex={1} cornerRadius="rounded" data-testid="modal-tertiary-btn" {...tertiaryButton}>
                            {tertiaryButton?.children}
                          </Button>
                        )}
                        {!!primaryButton && (
                          <Button tabIndex={2} cornerRadius="rounded" color="success" {...primaryButton}>
                            {primaryButton?.children}
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export interface ModalProps extends PropsWithChildren {
  id?: string;
  gap?: number;
  open: boolean;
  maxWidth?: number;
  freeForm?: boolean;
  className?: string;
  hasFooter?: boolean;
  onOpen?: () => void;
  topDivider?: boolean;
  onClose?: () => void;
  // Fires when the close transition completes in place. A Modal unmounted mid-transition never fires it.
  onClosed?: () => void;
  closeIconSize?: number;
  bottomDivider?: boolean;
  closeOnEscape?: boolean;
  decoupledScroll?: boolean;
  holderClassName?: string;
  title?: string | ReactNode;
  onClickOutside?: () => void;
  primaryButton?: ButtonProps;
  tertiaryButton?: ButtonProps;
  closeOnClickOutside?: boolean;
  secondaryButton?: ButtonProps;
  modalContentHolderClassName?: string;
}
